import { exportToCsv, exportToXlsx } from '../../../utils/exportFiles';

export { exportToCsv, exportToXlsx } from '../../../utils/exportFiles';

export function formatExportDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function calcSaldoValoradoLinea(producto, store) {
  const stock = Math.max(0, parseInt(producto?.stock, 10) || 0);
  const costo = store?.obtenerCostoPromedioProducto
    ? store.obtenerCostoPromedioProducto(producto.id)
    : parseFloat(producto?.precio_compra) || 0;
  return stock * Math.max(0, parseFloat(costo) || 0);
}

export function toLocalDateKey(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getDefaultExportDateFrom() {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${m}-01`;
}

export function getDefaultExportDateTo() {
  return toLocalDateKey(new Date());
}

export function isDateInRange(fecha, from, to) {
  const key = toLocalDateKey(fecha);
  if (!key) return false;
  if (from && key < from) return false;
  if (to && key > to) return false;
  return true;
}

export function mergeVentasFromStore(store) {
  const ventasMap = new Map((store?.ventas || []).map((v) => [v.id, { ...v, _inferred: false }]));

  (store?.detalleVentas || []).forEach((d) => {
    const ventaId = String(d.venta_id || '').trim();
    if (!ventaId || ventasMap.has(ventaId)) return;
    ventasMap.set(ventaId, {
      id: ventaId,
      fecha: null,
      metodo_pago: 'N/D',
      subtotal: 0,
      descuento_total: 0,
      total: 0,
      anulado: false,
      descuentos: [],
      _inferred: true,
    });
  });

  for (const venta of ventasMap.values()) {
    if (!venta._inferred) continue;
    const detalles = (store?.detalleVentas || []).filter((d) => d.venta_id === venta.id);
    const subtotal = detalles.reduce(
      (acc, d) => acc + (parseInt(d.cantidad, 10) || 0) * (parseFloat(d.precio_unitario) || 0),
      0
    );
    venta.subtotal = subtotal;
    venta.total = subtotal;
  }

  return [...ventasMap.values()].sort((a, b) => {
    const ta = a?.fecha ? new Date(a.fecha).getTime() : 0;
    const tb = b?.fecha ? new Date(b.fecha).getTime() : 0;
    return tb - ta;
  });
}

export function mergeComprasFromStore(store) {
  const comprasMap = new Map((store?.compras || []).map((c) => [c.id, { ...c, _inferred: false }]));

  (store?.detalleCompras || []).forEach((d) => {
    const compraId = String(d.compra_id || '').trim();
    if (!compraId || comprasMap.has(compraId)) return;
    comprasMap.set(compraId, {
      id: compraId,
      proveedor: '(sin proveedor)',
      total: 0,
      fecha: null,
      _inferred: true,
    });
  });

  for (const compra of comprasMap.values()) {
    const detalles = (store?.detalleCompras || []).filter((d) => d.compra_id === compra.id);
    if (compra._inferred || compra.total === undefined || compra.total === null) {
      compra.total = detalles.reduce(
        (acc, d) => acc + (parseInt(d.cantidad, 10) || 0) * (parseFloat(d.precio_compra) || 0),
        0
      );
    }
  }

  return [...comprasMap.values()].sort((a, b) => {
    const ta = a?.fecha ? new Date(a.fecha).getTime() : 0;
    const tb = b?.fecha ? new Date(b.fecha).getTime() : 0;
    return tb - ta;
  });
}

export function filterByDateRange(items, from, to) {
  return (items || []).filter((item) => isDateInRange(item.fecha, from, to));
}

export function buildExportMatrix(columns, orderedSelectedKeys, records, ctx = {}) {
  const colMap = new Map(columns.map((c) => [c.key, c]));
  const activeCols = orderedSelectedKeys.map((k) => colMap.get(k)).filter(Boolean);
  const headers = activeCols.map((c) => c.label);
  const rows = records.map((record) =>
    activeCols.map((colDef) => {
      if (colDef.getValue) {
        return colDef.getValue(record, { ...ctx, line: record.line, detalles: record.detalles });
      }
      const raw = record[colDef.key];
      if (colDef.key === 'fecha') return formatExportDate(raw);
      if (typeof raw === 'number') return raw;
      if (raw == null) return '';
      return raw;
    })
  );
  return { headers, rows };
}

export function runExport({ format, headers, rows, filenameBase, dateFrom, dateTo }) {
  const base = dateFrom && dateTo
    ? `${filenameBase}_${dateFrom}_${dateTo}`
    : `${filenameBase}_${new Date().toISOString().slice(0, 10)}`;
  if (format === 'xlsx') {
    exportToXlsx(headers, rows, `${base}.xlsx`, filenameBase);
  } else {
    exportToCsv(headers, rows, `${base}.csv`);
  }
}

export function buildInventarioRecords(productos) {
  return (productos || []).map((p) => ({ ...p }));
}

export function buildVentasHeaderRecords(ventas, detalleVentas) {
  return (ventas || []).map((v) => ({
    ...v,
    detalles: (detalleVentas || []).filter((d) => d.venta_id === v.id),
  }));
}

export function buildVentasDetailRecords(ventas, detalleVentas) {
  const ventasMap = new Map((ventas || []).map((v) => [v.id, v]));
  const lines = [];
  (detalleVentas || []).forEach((d) => {
    const venta = ventasMap.get(d.venta_id) || {};
    lines.push({
      ...venta,
      ...d,
      line: d,
      detalles: [d],
    });
  });
  return lines;
}

export function buildComprasHeaderRecords(compras, detalleCompras) {
  return (compras || []).map((c) => ({
    ...c,
    detalles: (detalleCompras || []).filter((d) => d.compra_id === c.id),
  }));
}

export function buildComprasDetailRecords(compras, detalleCompras) {
  const comprasMap = new Map((compras || []).map((c) => [c.id, c]));
  const lines = [];
  (detalleCompras || []).forEach((d) => {
    const compra = comprasMap.get(d.compra_id) || {};
    lines.push({
      ...compra,
      ...d,
      line: d,
      detalles: [d],
    });
  });
  return lines;
}

export function prepareExportPayload({
  dataset,
  mode,
  orderedSelectedKeys,
  columns,
  records,
  ctx,
  filenameBase,
  format,
  dateFrom,
  dateTo,
}) {
  const { headers, rows } = buildExportMatrix(columns, orderedSelectedKeys, records, ctx);
  runExport({ format, headers, rows, filenameBase, dateFrom, dateTo });
  return rows.length;
}
